import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'

import { TextInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgSmartAccount } from '~/types'
import { sendTrackingData } from '~/utils/tracking'

type RenameFormValues = {
  name: string
}

export const RenameAccountDialog = ({
  smartAccount,
  closeDialog,
}: {
  smartAccount: Pick<OrgSmartAccount, 'id' | 'name' | 'address'>
  closeDialog: () => void
}) => {
  const { serverPutRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RenameFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: smartAccount.name,
    },
  })
  const name = useWatch({ control, name: 'name' })

  const onSubmit = async (data: RenameFormValues) => {
    sendTrackingData('clickedEditSmartAccountName', {
      smartAccountId: smartAccount.id,
      name: data.name,
    })
    const result = await serverPutRequest({
      path: '/api/smart_accounts/edit/name',
      params: {
        smartAccountId: smartAccount.id,
        name: data.name,
      },
      processingMessage: 'Updating name...',
      successMessage: 'Name updated',
      errorMessage: 'Failed to update name',
    })
    if (result) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
      })
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.CONTACT_LOOKUP,
          activeOrgId,
          smartAccount.address,
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.IMPORTED_ACCOUNTS, activeOrgId],
      })
      closeDialog()
    }
  }

  return (
    <SplitsDialogContent
      id="renameSmartAccount"
      title="Rename account"
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Save',
        onClick: () => void handleSubmit(onSubmit)(),
        isDisabled:
          !name?.trim() || name === smartAccount.name || !!errors.name,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          control={control}
          inputName="name"
          placeholder="Treasury"
          autoFocus
        />
      </form>
    </SplitsDialogContent>
  )
}
